import { css } from 'styled-components';
import { breakpoints } from '../../design-system/tokens/breakpoints';

const PUBLIC_BASE = `${process.env.PUBLIC_URL || ''}/assets/dashboard/monuments`;

export interface MonumentWatermark {
  id: string;
  file: string;
  nameEn: string;
  nameGr: string;
}

export const MONUMENT_WATERMARKS: MonumentWatermark[] = [
  {
    id: 'acropolis-museum',
    file: 'icom-greece-acropolis-museum-watermark.png',
    nameEn: 'Acropolis Museum, Athens',
    nameGr: 'Μουσείο Ακρόπολης, Αθήνα',
  },
  {
    id: 'national-archaeological-museum',
    file: 'icom-greece-national-archaeological-museum-watermark.png',
    nameEn: 'National Archaeological Museum, Athens',
    nameGr: 'Εθνικό Αρχαιολογικό Μουσείο, Αθήνα',
  },
  {
    id: 'benaki-museum',
    file: 'icom-greece-benaki-museum-watermark.png',
    nameEn: 'Benaki Museum, Athens',
    nameGr: 'Μουσείο Μπενάκη, Αθήνα',
  },
  {
    id: 'byzantine-christian-museum',
    file: 'icom-greece-byzantine-christian-museum-watermark.png',
    nameEn: 'Byzantine and Christian Museum, Athens',
    nameGr: 'Βυζαντινό και Χριστιανικό Μουσείο, Αθήνα',
  },
  {
    id: 'national-historical-museum',
    file: 'icom-greece-national-historical-museum-watermark.png',
    nameEn: 'National Historical Museum, Athens',
    nameGr: 'Εθνικό Ιστορικό Μουσείο, Αθήνα',
  },
  {
    id: 'national-gallery',
    file: 'icom-greece-national-gallery-watermark.png',
    nameEn: 'National Gallery, Athens',
    nameGr: 'Εθνική Πινακοθήκη, Αθήνα',
  },
  {
    id: 'maria-callas-museum',
    file: 'icom-greece-maria-callas-museum-watermark.png',
    nameEn: 'Maria Callas Museum, Athens',
    nameGr: 'Μουσείο Μαρία Κάλλας, Αθήνα',
  },
  {
    id: 'maria-callas-museum-facade',
    file: 'icom-greece-maria-callas-museum-facade-watermark.png',
    nameEn: 'Maria Callas Museum, Athens',
    nameGr: 'Μουσείο Μαρία Κάλλας, Αθήνα',
  },
  {
    id: 'athens-university-history-museum',
    file: 'icom-greece-athens-university-history-museum-watermark.png',
    nameEn: 'Athens University History Museum',
    nameGr: 'Μουσείο Ιστορίας Πανεπιστημίου Αθηνών',
  },
  {
    id: 'modern-greek-culture-museum',
    file: 'icom-greece-modern-greek-culture-museum-watermark.png',
    nameEn: 'Museum of Modern Greek Culture, Athens',
    nameGr: 'Μουσείο Νεότερου Ελληνικού Πολιτισμού, Αθήνα',
  },
  {
    id: 'technopolis-athens',
    file: 'icom-greece-technopolis-athens-watermark.png',
    nameEn: 'Technopolis City of Athens',
    nameGr: 'Τεχνόπολη Δήμου Αθηναίων',
  },
  {
    id: 'thessaloniki-archaeological-museum',
    file: 'icom-greece-thessaloniki-archaeological-museum-watermark.png',
    nameEn: 'Archaeological Museum of Thessaloniki',
    nameGr: 'Αρχαιολογικό Μουσείο Θεσσαλονίκης',
  },
  {
    id: 'heraklion-archaeological-museum',
    file: 'icom-greece-heraklion-archaeological-museum-watermark.png',
    nameEn: 'Heraklion Archaeological Museum',
    nameGr: 'Αρχαιολογικό Μουσείο Ηρακλείου',
  },
  {
    id: 'chalkis-archaeological-museum',
    file: 'icom-greece-chalkis-archaeological-museum-watermark.png',
    nameEn: 'Archaeological Museum of Chalkis',
    nameGr: 'Αρχαιολογικό Μουσείο Χαλκίδας',
  },
  {
    id: 'patras-science-museum',
    file: 'icom-greece-patras-science-museum-watermark.png',
    nameEn: 'Science and Technology Museum, Patras',
    nameGr: 'Μουσείο Επιστημών και Τεχνολογίας, Πάτρα',
  },
];

export const MONUMENT_WATERMARK_CSS_VAR = '--icom-monument-watermark';

/** Remembers the last shown museum so a refresh usually picks a different one. */
const LAST_SHOWN_KEY = 'icom.monumentWatermarkLastId';

/** Stable for the current page load only (survives SPA navigations, resets on refresh). */
let pageLoadMonument: MonumentWatermark | null = null;

export function getMonumentWatermarkUrl(monument: MonumentWatermark): string {
  return `${PUBLIC_BASE}/${monument.file}`;
}

export function getMonumentById(id: string | null | undefined): MonumentWatermark | undefined {
  if (!id) return undefined;
  return MONUMENT_WATERMARKS.find(m => m.id === id);
}

function pickRandomMonument(excludeId?: string | null): MonumentWatermark {
  const pool =
    excludeId != null
      ? MONUMENT_WATERMARKS.filter(m => m.id !== excludeId)
      : MONUMENT_WATERMARKS;
  const list = pool.length > 0 ? pool : MONUMENT_WATERMARKS;
  const index = Math.floor(Math.random() * list.length);
  return list[index] ?? MONUMENT_WATERMARKS[0];
}

/**
 * One random museum per page load (new on every refresh).
 * Shared across dashboard/profile during SPA navigation within that load.
 */
export function pickPageLoadMonument(): MonumentWatermark {
  if (pageLoadMonument) return pageLoadMonument;

  if (typeof window === 'undefined') {
    pageLoadMonument = MONUMENT_WATERMARKS[0];
    return pageLoadMonument;
  }

  let lastId: string | null = null;
  try {
    lastId = window.sessionStorage.getItem(LAST_SHOWN_KEY);
  } catch {
    /* ignore */
  }

  const next = pickRandomMonument(lastId);
  pageLoadMonument = next;

  try {
    window.sessionStorage.setItem(LAST_SHOWN_KEY, next.id);
  } catch {
    /* ignore */
  }

  return next;
}

/** @deprecated Use pickPageLoadMonument — kept for existing imports. */
export function pickSessionMonument(): MonumentWatermark {
  return pickPageLoadMonument();
}

export function getMonumentWatermarkStyle(
  monument: MonumentWatermark
): Record<string, string> {
  return {
    [MONUMENT_WATERMARK_CSS_VAR]: `url('${getMonumentWatermarkUrl(monument)}')`,
  };
}

/** @deprecated Prefer page-load-random museums via useMonumentWatermark. */
export const DASHBOARD_WATERMARK_URL = getMonumentWatermarkUrl(MONUMENT_WATERMARKS[0]);

/** Header/hero watermark — background driven by --icom-monument-watermark */
export const headerWatermarkStyles = css`
  background: ${props => props.theme.colors.surface};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.surface} 0%,
      ${props => props.theme.colors.surface} 34%,
      transparent 72%
    );
  }

  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    /* Fit the whole plate inside the header so tall facades are not clipped. */
    top: 10px;
    bottom: 34px;
    right: 8px;
    width: auto;
    height: auto;
    max-width: min(760px, 62%);
    aspect-ratio: 1024 / 409;
    background-image: var(
      ${MONUMENT_WATERMARK_CSS_VAR},
      url('${PUBLIC_BASE}/icom-greece-acropolis-museum-watermark.png')
    );
    background-repeat: no-repeat;
    background-position: right center;
    background-size: contain;
    pointer-events: none;
    user-select: none;
    /* Light theme: ink-on-paper look */
    opacity: 0.3;
    filter: grayscale(0.3) contrast(1.08) saturate(0.9);
    mix-blend-mode: multiply;
    -webkit-mask-image: linear-gradient(
      to left,
      #000 42%,
      rgba(0, 0, 0, 0.75) 68%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to left,
      #000 42%,
      rgba(0, 0, 0, 0.75) 68%,
      transparent 100%
    );
  }

  html[data-theme='dark'] &::after {
    opacity: 0.22;
    filter: grayscale(0.35) brightness(1.3) contrast(1.05) saturate(0.75);
    mix-blend-mode: soft-light;
  }

  @media (max-width: ${breakpoints.md}) {
    &::after {
      right: 4px;
      bottom: 8px;
      max-width: min(400px, 72%);
      opacity: 0.24;
    }

    html[data-theme='dark'] &::after {
      opacity: 0.18;
    }
  }

  @media (prefers-contrast: more) {
    &::after {
      display: none;
    }
  }
`;
